/**
 * The URL a page's Open Graph card is served from (VIT-18196).
 *
 * A card lives at its page's own path, prefixed with `/og` and suffixed with `.png`:
 *
 *     /us/provider/npi-1861770604-jane-doe  →  /og/us/provider/npi-1861770604-jane-doe.png
 *
 * Two things fall out of that shape, which is why it was chosen over naming the template
 * in a query string:
 *
 *  - **The card is guessable from the page and vice versa.** A crawler log, a Slack
 *    unfurl and a CloudFront access log all show which page a render belongs to without
 *    anyone decoding a query string.
 *  - **Invalidation composes.** `/og/us/provider/npi-<npi>*` clears every slug spelling
 *    of one provider's card, exactly as `/us/provider/npi-<npi>*` does for the pages
 *    (VIT-17797, docs/url-structure.md).
 *
 * `/og` rather than nesting under `/us`: CloudFront matches the FIRST behavior in its
 * ordered list rather than the most specific, so `/us/provider/*​/og.png` would have to be
 * ordered ahead of `/us/*` to be reachable at all — and it points at a different origin
 * (the renderer, not the SSR site), which is exactly the kind of ordering dependency that
 * breaks quietly when a behavior is added later.
 *
 * Pure — no `fs`, no `fetch`. The page's `meta` builds a card URL with `cardPath`, and the
 * renderer parses one back with `pagePathFromCard`, so the two cannot disagree about the
 * shape.
 */

/** Everything the renderer serves sits under this prefix — it is one CloudFront behavior. */
export const OG_CARD_PREFIX = '/og'

/**
 * `.png` because some crawlers sniff the extension before they will fetch an image at all,
 * and because it is what the renderer actually returns.
 */
export const OG_CARD_EXTENSION = '.png'

/** The card URL for a page path. The page path is expected absolute, as `href()` returns it. */
export const cardPath = (pagePath: string): string => `${OG_CARD_PREFIX}${pagePath}${OG_CARD_EXTENSION}`

/**
 * The one path under the prefix that is NOT a page's card: the fixed-content contract,
 * where the card is described by the query string (`?template=generic&…`) because its copy
 * lives in the repo rather than in an upstream record.
 *
 * Reserved here rather than special-cased in the renderer, so `/card` can never also be
 * read as a page path — and so a page route named `/card` would collide loudly in this
 * file instead of quietly shadowing the contract.
 */
export const OG_QUERY_CARD_PATH = `${OG_CARD_PREFIX}/card${OG_CARD_EXTENSION}`

/**
 * The page path a card URL describes, or null when the path is not a card URL at all.
 *
 * Deliberately strict — an unrecognised path is a 404 rather than a guess, because this
 * endpoint is public and every 200 costs a render. Rejects anything that is not
 * `/og/<something>.png`, and rejects a bare `/og.png` (no page path) or an empty page
 * segment.
 *
 * Takes the raw path only; a caller strips the query string first. `.png` is matched
 * case-sensitively: the URLs are ours, emitted by `cardPath`, and accepting `.PNG` would
 * add a second cache key for the same render.
 */
export const pagePathFromCard = (path: string): string | null => {
  if (path === OG_QUERY_CARD_PATH || !path.startsWith(`${OG_CARD_PREFIX}/`) || !path.endsWith(OG_CARD_EXTENSION)) {
    return null
  }

  const pagePath = path.slice(OG_CARD_PREFIX.length, -OG_CARD_EXTENSION.length)

  // `/og/.png` slices to `/`, which is not a page this renderer serves a card for.
  return pagePath === '/' ? null : pagePath
}

/**
 * The provider whose card a page path asks for — the `npi-<npi>-<slug>` segment, still
 * unparsed, or null when the path is not a provider page.
 *
 * The segment is handed back rather than the NPI so the caller runs the SAME
 * `parseProviderNpi` the page's loader does (`app/slug.ts`), instead of this file growing
 * a second, subtly different idea of what a provider URL looks like.
 */
export const providerSegmentFromPagePath = (pagePath: string): string | null =>
  /^\/us\/provider\/([^/]+)$/u.exec(pagePath)?.[1] ?? null

/**
 * The location and specialty segments a results page path asks for, or null when the path
 * is not a results page (VIT-18197).
 *
 * Both segments are handed back unparsed, for the same reason the provider one is: the
 * caller runs the SAME `parseLocation` / `specialtyForSlug` the page's loader does, rather
 * than this file growing a second idea of what a results URL looks like.
 *
 * `/us/provider/<segment>` matches this shape too — it is two segments under `/us` like any
 * other. That is not disambiguated here, because it cannot be: `provider` is a reserved
 * first segment in the URL grammar (docs/url-structure.md) rather than a location, and no
 * location slug can spell it (`parseLocation` requires a ZIP or an `intl-` prefix). The
 * renderer tries the provider branch first and never falls through to this one, which is
 * where the precedence is stated.
 */
export const resultsSegmentsFromPagePath = (pagePath: string): { location: string; specialty: string } | null => {
  const match = /^\/us\/([^/]+)\/([^/]+)$/u.exec(pagePath)

  return match?.[1] == null || match[2] == null ? null : { location: match[1], specialty: match[2] }
}
